import { useState } from 'react';
import {
  loadCharacters,
  setActiveId,
  createCharacter,
  upsertCharacter,
  deleteCharacter,
} from '../../lib/characterCards.js';
import {
  loadPresets,
  setActivePresetId,
  createPreset,
  upsertPreset,
  deletePreset,
} from '../../lib/presets.js';
import {
  loadEntries,
  createEntry,
  upsertEntry,
  deleteEntry,
  toggleEntry,
} from '../../lib/lorebook.js';
import CurrentTab from './settings/CurrentTab.jsx';
import { CharactersList, CharacterEditor } from './settings/CharactersTab.jsx';
import { PresetsList, PresetEditor } from './settings/PresetsTab.jsx';
import { LorebookList, LorebookEditor } from './settings/LorebookTab.jsx';

/* línkè · 聊天 - 设定面板（壳）
   tab: 当前 / 角色卡 / 预设 / 世界书 —— 各 tab 的界面拆在 ./settings/ 下，
   这里只管 tab 切换和资源回调。父组件控制各资源的 state，本组件只发回调。 */

export default function SettingsPanel({
  characters,
  activeId,
  presets,
  activePresetId,
  lorebook,
  providerKey,
  providerBase,
  providerModel,
  wallpaper,
  onWallpaperChange,
  onCharactersChange,
  onActiveChange,
  onPresetsChange,
  onActivePresetChange,
  onLorebookChange,
  onProviderChange,
  onResetDefaults,
  onClose,
}) {
  const [tab, setTab] = useState('current');
  // 编辑状态：{ type: 'character' | 'preset' | 'lore', id: string } | null
  const [editing, setEditing] = useState(null);

  function refreshChars() { onCharactersChange(loadCharacters()); }
  function refreshPresets() { onPresetsChange(loadPresets()); }
  function refreshLore() { onLorebookChange(loadEntries()); }

  // —— 角色卡 ——
  function handleActivate(id) {
    setActiveId(id);
    onActiveChange(id);
  }
  function handleNewCharacter() {
    const draft = createCharacter({ name: '新角色', avatar: '✦' });
    upsertCharacter(draft);
    refreshChars();
    setEditing({ type: 'character', id: draft.id });
  }
  function handleSaveCharacter(draft) {
    upsertCharacter(draft);
    refreshChars();
    setEditing(null);
  }
  function handleDeleteCharacter(id) {
    if (!window.confirm('删除这张角色卡？')) return;
    deleteCharacter(id);
    refreshChars();
    onActiveChange(loadCharacters()[0]?.id || null);
  }

  // —— 预设 ——
  function handleActivatePreset(id) {
    setActivePresetId(id);
    onActivePresetChange(id);
  }
  function handleNewPreset() {
    const draft = createPreset({ name: '新预设' });
    upsertPreset(draft);
    refreshPresets();
    setEditing({ type: 'preset', id: draft.id });
  }
  function handleSavePreset(draft) {
    upsertPreset(draft);
    refreshPresets();
    setEditing(null);
  }
  function handleDeletePreset(id) {
    if (!window.confirm('删除这个预设？')) return;
    deletePreset(id);
    refreshPresets();
    onActivePresetChange(loadPresets()[0]?.id || null);
  }

  // —— 世界书 ——
  function handleNewLore() {
    const draft = createEntry({ keys: [], content: '' });
    upsertEntry(draft);
    refreshLore();
    setEditing({ type: 'lore', id: draft.id });
  }
  function handleSaveLore(draft) {
    upsertEntry(draft);
    refreshLore();
    setEditing(null);
  }
  function handleDeleteLore(id) {
    if (!window.confirm('删除这个条目？')) return;
    deleteEntry(id);
    refreshLore();
  }
  function handleToggleLore(id) {
    toggleEntry(id);
    refreshLore();
  }

  const editingChar =
    editing?.type === 'character'
      ? characters.find((c) => c.id === editing.id)
      : null;
  const editingPreset =
    editing?.type === 'preset'
      ? presets.find((p) => p.id === editing.id)
      : null;
  const editingLore =
    editing?.type === 'lore'
      ? lorebook.find((e) => e.id === editing.id)
      : null;

  function switchTab(t) {
    setTab(t);
    setEditing(null);
  }

  return (
    <div className="ch-settings">
      {/* —— Tab 切换 —— */}
      <div className="cc-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'current'}
          className={'cc-tab' + (tab === 'current' ? ' on' : '')}
          onClick={() => switchTab('current')}
        >
          当 前
        </button>
        <button
          role="tab"
          aria-selected={tab === 'characters'}
          className={'cc-tab' + (tab === 'characters' ? ' on' : '')}
          onClick={() => switchTab('characters')}
        >
          角色卡
        </button>
        <button
          role="tab"
          aria-selected={tab === 'presets'}
          className={'cc-tab' + (tab === 'presets' ? ' on' : '')}
          onClick={() => switchTab('presets')}
        >
          预 设
        </button>
        <button
          role="tab"
          aria-selected={tab === 'lore'}
          className={'cc-tab' + (tab === 'lore' ? ' on' : '')}
          onClick={() => switchTab('lore')}
        >
          世界书
        </button>
      </div>

      {/* —— Tab: 当前 —— */}
      {tab === 'current' && (
        <CurrentTab
          characters={characters}
          activeId={activeId}
          presets={presets}
          activePresetId={activePresetId}
          lorebook={lorebook}
          onActivate={handleActivate}
          onActivatePreset={handleActivatePreset}
          onGotoCharacters={() => setTab('characters')}
          onGotoPresets={() => setTab('presets')}
          onGotoLore={() => setTab('lore')}
          providerKey={providerKey}
          providerBase={providerBase}
          providerModel={providerModel}
          wallpaper={wallpaper}
          onWallpaperChange={onWallpaperChange}
          onProviderChange={onProviderChange}
          onResetDefaults={onResetDefaults}
          onClose={onClose}
        />
      )}

      {/* —— Tab: 角色卡 —— */}
      {tab === 'characters' && !editingChar && (
        <CharactersList
          characters={characters}
          activeId={activeId}
          onActivate={handleActivate}
          onEdit={(id) => setEditing({ type: 'character', id })}
          onDelete={handleDeleteCharacter}
          onNew={handleNewCharacter}
          onClose={onClose}
        />
      )}
      {tab === 'characters' && editingChar && (
        <CharacterEditor
          initial={editingChar}
          onBack={() => setEditing(null)}
          onSave={handleSaveCharacter}
        />
      )}

      {/* —— Tab: 预设 —— */}
      {tab === 'presets' && !editingPreset && (
        <PresetsList
          presets={presets}
          activeId={activePresetId}
          onActivate={handleActivatePreset}
          onEdit={(id) => setEditing({ type: 'preset', id })}
          onDelete={handleDeletePreset}
          onNew={handleNewPreset}
          onClose={onClose}
        />
      )}
      {tab === 'presets' && editingPreset && (
        <PresetEditor
          initial={editingPreset}
          onBack={() => setEditing(null)}
          onSave={handleSavePreset}
        />
      )}

      {/* —— Tab: 世界书 —— */}
      {tab === 'lore' && !editingLore && (
        <LorebookList
          entries={lorebook}
          onEdit={(id) => setEditing({ type: 'lore', id })}
          onDelete={handleDeleteLore}
          onToggle={handleToggleLore}
          onNew={handleNewLore}
          onClose={onClose}
        />
      )}
      {tab === 'lore' && editingLore && (
        <LorebookEditor
          initial={editingLore}
          onBack={() => setEditing(null)}
          onSave={handleSaveLore}
        />
      )}
    </div>
  );
}

